import React, { useCallback, useContext, useState } from "react";
import clsx from "clsx";

import {
  NostoContext,
  type NostoContextProps,
} from "~/providers/NostoProvider";

import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

import { FacetSectionBox } from "./Facets/FacetSectionBox";
import { FacetMultiList } from "./Facets/FacetMultiList";
import { FacetSwatch } from "./Facets/FacetSwatch";
import { Sort } from "./Sort";

import { Sheet } from "~/components/Sheet";
import { AccordionAnimation } from "~/components/Accordion/AccordionAnimation";

import {
  Button,
  IconChevron,
  IconCross,
  IconFilter,
  useProductImage,
} from "@bared/ui";

export const Filters = () => {
  const {
    facets,
    productCount,
    selectedFilters,
    sortOptions,
    sortOption,
    setSortOption,
    setSelectedFilters,
    handleFilterChange,
    handleFilterRemove,
    gender,
    showViewToggle,
  } = useContext<NostoContextProps>(NostoContext);

  const googleTagManager = useGoogleTagManager();

  const { showHoverImages, toggleHoverImages } = useProductImage();

  const toggleHoverImageLabel = showHoverImages ? "Product View" : "Model View";

  const onToggleHoverImage = useCallback(() => {
    toggleHoverImages();

    googleTagManager.push({
      event: "Button Clicked",
      properties: {
        label: toggleHoverImageLabel,
      },
    });
  }, [toggleHoverImages, googleTagManager, toggleHoverImageLabel]);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleSheetToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const productCountLabel = productCount > 1 ? " products" : " product";

  const handleClearAllFilters = useCallback(() => {
    setSelectedFilters([], true);
  }, [setSelectedFilters]);

  const handleApplyFilters = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <div className="sticky top-[--header-height] z-40 flex justify-between bg-neutral p-mini md:p-midi">
        <Button
          variant="plain"
          size="small"
          onClick={handleSheetToggle}
          className="flex items-center gap-micro text-hint font-medium leading-hint hover:text-secondary"
        >
          <IconFilter className="w-[10px]" />
          {selectedFilters.length > 0
            ? `Filters & Sort (${selectedFilters.length})`
            : "Filters & Sort"}
        </Button>

        {showViewToggle && (
          <Button
            variant="secondary"
            size="xsmall"
            className="p-0 z-50 w-[95px]"
            onClick={onToggleHoverImage}
            aria-label="Toggle hover images"
          >
            {toggleHoverImageLabel}
          </Button>
        )}
      </div>

      <Sheet
        isOpen={isOpen}
        onClose={handleSheetToggle}
        eventTitle="Filters & Sort"
        layout="condensed"
        title="Filters & Sort"
        hideMode="css"
        className="flex flex-col"
      >
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1 overflow-y-auto">
            <SortSection
              sortOptions={sortOptions}
              sortOption={sortOption}
              setSortOption={setSortOption}
            />

            {facets
              .filter((facet) => {
                const facetNameLower = facet.name.toLocaleLowerCase();
                return (
                  facetNameLower.includes("subcategory") ||
                  facetNameLower.includes("size") ||
                  facetNameLower.includes("heel height") ||
                  facetNameLower.includes("colour")
                );
              })
              .map((facet) => {
                // Create a curried handleChange for this specific facet
                const handleFacetChange = (value: string) =>
                  handleFilterChange(facet.field, facet.name, value);

                const facetNameLower = facet.name.toLocaleLowerCase();

                // Calculate selected count for this filter
                const selectedCount =
                  selectedFilters.find((filter) => filter.field === facet.field)
                    ?.value.length ?? 0;

                return (
                  <FilterSection
                    name={facet.name}
                    key={facet.field}
                    selectedCount={selectedCount}
                  >
                    {facetNameLower === "size" && (
                      <FacetSectionBox
                        data={facet.data}
                        gender={gender}
                        handleChange={handleFacetChange}
                        field={facet.field}
                        selectedFilters={selectedFilters}
                      />
                    )}
                    {facetNameLower === "colour" && (
                      <FacetSwatch
                        data={facet.data}
                        handleChange={handleFacetChange}
                        field={facet.field}
                        selectedFilters={selectedFilters}
                      />
                    )}
                    {facetNameLower !== "size" &&
                      facetNameLower !== "colour" && (
                        <FacetMultiList
                          data={facet.data}
                          handleChange={handleFacetChange}
                          field={facet.field}
                          selectedFilters={selectedFilters}
                        />
                      )}
                  </FilterSection>
                );
              })}
          </div>

          <SheetFooter
            productCount={productCount}
            productCountLabel={productCountLabel}
            selectedFilters={selectedFilters}
            handleApplyFilters={handleApplyFilters}
            handleClearAllFilters={handleClearAllFilters}
            handleFilterRemove={handleFilterRemove}
          />
        </div>
      </Sheet>
    </>
  );
};

function SortSection({
  sortOptions,
  sortOption,
  setSortOption,
}: {
  sortOptions: NostoContextProps["sortOptions"];
  sortOption: NostoContextProps["sortOption"];
  setSortOption: NostoContextProps["setSortOption"];
}) {
  const [show, setShow] = useState<boolean>(false);

  const handleSectionToggle = useCallback(() => {
    setShow((prev) => !prev);
  }, []);

  return (
    <Sort
      sortOptions={sortOptions}
      sortOption={sortOption}
      setSortOption={setSortOption}
      show={show}
      handleSectionToggle={handleSectionToggle}
    />
  );
}

function FilterSection({
  children,
  name,
  selectedCount,
}: {
  children: React.ReactNode;
  name: string;
  selectedCount: number;
}) {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const handleSectionToggle = useCallback((handle: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(handle)) {
        newSet.delete(handle);
      } else {
        newSet.add(handle);
      }
      return newSet;
    });
  }, []);

  const show = openSections.has(name);

  return (
    <div className={clsx(show && "border-b", "w-full overflow-hidden")}>
      <div
        className="transition-presentation flex w-full items-center justify-start border-b hover:text-secondary"
        onClick={() => handleSectionToggle(name)}
      >
        <button className="grow px-mini py-maxi text-left">
          <span>{name}</span>
          {selectedCount > 0 && (
            <span className="ml-mini text-hint text-primary">
              {selectedCount}
            </span>
          )}
        </button>
        <IconChevron
          className={clsx(
            show && "rotate-180",
            "mx-mini mt-pico w-[12px] transform transform-gpu",
          )}
        />
      </div>
      <AccordionAnimation active={show}>{children}</AccordionAnimation>
    </div>
  );
}

type SheetFooterProps = {
  /** The number of products that match the selected filters */
  productCount: number;
  /** The label for the product count */
  productCountLabel: string;
  /** The selected filters */
  selectedFilters: NostoContextProps["selectedFilters"];
  /** The function to handle the apply filters button */
  handleApplyFilters: () => void;
  /** The function to handle the clear all filters button */
  handleClearAllFilters: () => void;
  /** The function to handle the filter remove button */
  handleFilterRemove: (field: string, value: string) => void;
};

function SheetFooter({
  productCount,
  productCountLabel,
  selectedFilters,
  handleApplyFilters,
  handleClearAllFilters,
  handleFilterRemove,
}: SheetFooterProps) {
  return (
    <div className="w-full space-y-midi border-t border-primary-300 px-mini py-midi">
      <div className="before:bg-[linear-gradient(to right, rgba(243,241,237,0) 0%,rgba(243,241,237,1) 100%)] flex flex-col before:pointer-events-none before:absolute before:right-zero before:z-10 before:block before:h-mega before:w-mega">
        <ul
          className="hide-scrollbar flex w-full list-none space-x-[10px] overflow-x-auto overflow-y-hidden"
          data-testid="selected-scroll-wrapper"
        >
          {selectedFilters.flatMap((filter) =>
            filter.value.map((value) => (
              <li
                key={`${filter.field}-${value}`}
                className="flex-shrink-0 justify-center gap-[10px]"
              >
                <Button
                  variant="plain"
                  size="xsmall"
                  className="relative flex cursor-pointer border px-micro py-micro text-center text-hint leading-input"
                  onClick={() => handleFilterRemove(filter.field, value)}
                >
                  <span className="block capitalize">{value}</span>
                  <IconCross className="block w-mini pl-[10px]" />
                </Button>
              </li>
            )),
          )}
        </ul>
      </div>

      <Button
        variant="primary"
        size="large"
        onClick={handleApplyFilters}
        className="w-full"
      >
        Apply Filters
      </Button>
      <div className="flex items-center justify-between">
        <div>
          {selectedFilters.length > 0 && (
            <Button
              className="text-hint font-medium leading-hint underline"
              variant="plain"
              size="small"
              onClick={handleClearAllFilters}
            >
              Reset Filters
            </Button>
          )}
        </div>

        <div>
          <div
            data-testid="product-stats-label"
            className="text-hint text-primary"
          >
            {productCount > 0 ? productCount + productCountLabel : null}
          </div>
        </div>
      </div>
    </div>
  );
}
